import React from 'react';
import { Network, Activity, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface SourceParams {
  name: string;
  itemsLastHour: number;
  lastIngested: string;
}

interface IngestionStats {
  status: 'healthy' | 'degraded' | 'unknown';
  itemsPerMinute: number;
  sources: SourceParams[];
  lastCheck: string;
}

interface IngestionFlowWidgetProps {
  data?: IngestionStats;
}

export const IngestionFlowWidget: React.FC<IngestionFlowWidgetProps> = ({ data }) => {
  if (!data) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Network className="w-5 h-5 text-indigo-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Data Ingestion Flow</h3>
        </div>
        <div className="flex items-center text-sm text-gray-500">
          <Activity className="w-4 h-4 mr-1" />
          {data.itemsPerMinute.toFixed(1)} items/min
        </div>
      </div>

      <div className="space-y-3">
        {data.sources.map((source) => {
          const lastIngestedDate = new Date(source.lastIngested);
          const isStale = Date.now() - lastIngestedDate.getTime() > 1000 * 60 * 60; // > 1 hour

          return (
            <div key={source.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-3 ${isStale ? 'bg-yellow-400' : 'bg-green-500'}`} />
                <div>
                  <div className="font-medium text-sm text-gray-900">{source.name}</div>
                  <div className="text-xs text-gray-500">
                    {source.itemsLastHour} items/hr
                  </div>
                </div>
              </div>
              <div className="flex items-center text-xs text-gray-500">
                <Clock className="w-3 h-3 mr-1" />
                {formatDistanceToNow(lastIngestedDate, { addSuffix: true })}
              </div>
            </div>
          );
        })}

        {data.sources.length === 0 && (
          <div className="text-center py-4 text-gray-500 text-sm">
            No active data sources detected in the last hour
          </div>
        )}
      </div>
    </div>
  );
};
